import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';
import type { SuiteAlertConfig } from '../test-suites/types';

type SystemAlertRule = { id: string; enabled: boolean; alertConfig: SuiteAlertConfig };

const systemAlertKey = (triggerEvent: string) => ['system-alert-rule', triggerEvent] as const;

/**
 * An org-wide alert: the org's one `system` / `<triggerEvent>` notify rule (a
 * unique index guarantees at most one). `data` is null when no rule exists yet.
 */
export function useSystemAlertRule(triggerEvent: string, enabled: boolean) {
  return useQuery({
    queryKey: systemAlertKey(triggerEvent),
    enabled,
    queryFn: async (): Promise<SystemAlertRule | null> => {
      const res = await fetch(
        `${API_URL}/org/automation/rules?triggerSource=system&actionType=notify&triggerEvent=${triggerEvent}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw await toApiError(res, 'Failed to load alerts');
      const body: { data: SystemAlertRule[] } = await res.json();
      const rule = body.data[0];
      return rule ? { id: rule.id, enabled: rule.enabled, alertConfig: rule.alertConfig } : null;
    },
  });
}

/** Save an org-wide alert: update the existing rule, or create it (named `name`) when none exists. */
export function useSaveSystemAlertRule(triggerEvent: string, name: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ruleId, alertConfig }: { ruleId: string | null; alertConfig: SuiteAlertConfig }) => {
      const res = ruleId
        ? await fetch(`${API_URL}/org/automation/rules/${ruleId}`, {
            method: 'PUT',
            credentials: 'include',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ alertConfig, enabled: true }),
          })
        : await fetch(`${API_URL}/org/automation/rules`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              name,
              enabled: true,
              triggerSource: 'system',
              triggerEvent,
              actionType: 'notify',
              alertConfig,
            }),
          });
      if (!res.ok) throw await toApiError(res, 'Failed to save alerts');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: systemAlertKey(triggerEvent) }),
  });
}
